import React, { useState } from 'react'
import PropTypes from 'prop-types'
import IconButton from '@material-ui/core/IconButton'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  ContextMenuListItem,
  Popover,
} from '../../../components/common/Dialogs/Popover'
import ListItemTextBase from '../../../components/common/Containers/Base/ListItemTextBase'
import ConfirmDialog from '../../../components/generics/Confirm/Dialog'
import ResetPasswordModal from '../../RegisteredUsers/components/ResetPasswordModal'
import Typography from '../../../components/common/Typography'
import usePopover from '../../../hooks/usePopover'
import useBooleanHandlers from '../../../hooks/useBooleanHandlers'
import { useTrans } from '../../../services/i18n'
import { useAlertContext } from '../../../contexts/AlertContext'
import { application } from '../../../services/application'
import { API } from '../../../constants'
import { selectUserPermissions, isEnabled } from '../../../selectors'

const DELETE_NOTE =
  'The following items will be deleted permanently: User settings, User data, Pipelines, Pursuits, Teaming opportunities, Uploaded documents, Forms, and Organizations in which this user has the role of "Owner". This could affect other users too as they will lose access to the aforementioned items if they were previously shared with the user that is about to be deleted. If you agree, click Confirm to delete this user from the platform.'

const UserMenu = ({ user }) => {
  const { trans } = useTrans()
  const history = useNavigate()
  const [anchorEl, onOpenPopover, onClosePopover] = usePopover()
  const [openReset, onOpenReset, onCloseReset] = useBooleanHandlers()
  const [openConfirmDelete, onConfirmDelete, onCloseConfirmDelete] =
    useBooleanHandlers()
  const [pendingDelete, setPendingDelete] = useState(false)
  const { successAlert, errorAlert } = useAlertContext()
  const permissions = useSelector(selectUserPermissions)

  const fullName =
    user.name === 'Unassigned' ? '' : `${user.name} ${user.familyName}`
  const canEdit = isEnabled(permissions, 'registered-users', '', 'edit')
  const canDelete = isEnabled(permissions, 'registered-users', '', 'delete')

  const onDelete = () => {
    setPendingDelete(true)
    application
      .call(API.USERS.DELETE, { id: user.id })
      .then(() => {
        onCloseConfirmDelete()
        successAlert(`${fullName} has been removed`)
        history('/users')
      })
      .catch(err => {
        errorAlert(err.message)
      })
      .finally(() => setPendingDelete(false))
  }

  const onResendVerification = async () => {
    try {
      const response = await application.call(API.USERS.RESEND_VERIFICATION, {
        email: user.email,
      })
      if (response?.success) {
        successAlert(`${fullName}'s verification email has been resent`)
      } else {
        errorAlert(`Error: ${response.message}`)
      }
    } catch (err) {
      errorAlert(`Failed to resend verification: ${err.message}`)
    }
  }

  if (!canEdit && !canDelete) return null

  return (
    <>
      <IconButton onClick={onOpenPopover}>
        <MoreHorizIcon />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClosePopover}
      >
        {canEdit && (
          <>
            <ContextMenuListItem
              button
              onClick={onOpenReset}
              onClickCB={onClosePopover}
            >
              <ListItemTextBase>
                {trans('forgot-password-sentence')}
              </ListItemTextBase>
            </ContextMenuListItem>
            {user.isVerified === 0 && (
              <ContextMenuListItem
                button
                onClick={onResendVerification}
                onClickCB={onClosePopover}
              >
                <ListItemTextBase>
                  {trans('resend-verification')}
                </ListItemTextBase>
              </ContextMenuListItem>
            )}
          </>
        )}
        {canDelete && !user.isOwner && (
          <ContextMenuListItem
            button
            onClick={onConfirmDelete}
            onClickCB={onClosePopover}
          >
            <ListItemTextBase>{trans('delete-user')}</ListItemTextBase>
          </ContextMenuListItem>
        )}
      </Popover>
      <ResetPasswordModal item={user} open={openReset} onClose={onCloseReset} />
      <ConfirmDialog
        open={openConfirmDelete}
        onClose={onCloseConfirmDelete}
        onConfirm={onDelete}
        pending={pendingDelete}
        title={`${trans('delete-user')} ${fullName}`}
        text={trans('confirm-delete-user')}
        note={DELETE_NOTE}
      >
        <Typography color="hint" weight="bold">
          {fullName}
        </Typography>
        <Typography color="hint">{user.email}</Typography>
      </ConfirmDialog>
    </>
  )
}

UserMenu.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    familyName: PropTypes.string,
    email: PropTypes.string,
    isVerified: PropTypes.number,
    isOwner: PropTypes.bool,
  }).isRequired,
}

export default UserMenu
